import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, BarChart2, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { BarList, EmptyState, type BarListItem } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import {
  useAesAnalytics,
  useDrmUsageSummary,
  type AesAnalyticsMetric,
  type AesAnalyticsPoint,
  type AesAnalyticsGranularity,
  type DrmUsageRow,
} from '@/api/aes';
import { Stack, KpiTiles, RefreshControl, type Kpi } from '../_shared/AnalyticsKit';
import { DateTimeRangePicker } from '@/components/date-time-range-picker/DateTimeRangePicker';
import { formatNum, formatCount } from '../_shared/analytics-format';
import styles from './AesUsage.module.css';
import drmStyles from './drm/Drm.module.css';

type UsageScope = 'gateway' | 'self-hosted';

/**
 * Business Usage — ONE shared page with an in-page scope toggle, so there is a
 * single "Business Usage" entry (no per-mode duplicates):
 *
 *   'gateway'      gateway-wide datalake time-series (keys / sessions / unique
 *                  IPs) across CDN + self-hosted, via @mgmt analytics. Period is
 *                  a preset window, a calendar month, or a custom day range so a
 *                  past billing period can be inspected; the current view exports
 *                  to CSV.
 *   'self-hosted'  per-stream rollup for the caller's own packager streams, via
 *                  sig-control GET /usage/summary.
 *
 * The scope lives in `?scope=` so a deep link (e.g. the DRM overview's "view
 * usage") can land directly on the self-hosted view. Each scope keeps its own
 * bucket/period semantics and visualization; the shared toolbar (period + refresh)
 * and KPI/format helpers come from AnalyticsKit.
 */
export function UsageAnalytics({ serviceId }: { serviceId?: string }) {
  const [params, setParams] = useSearchParams();
  const scope: UsageScope = params.get('scope') === 'self-hosted' ? 'self-hosted' : 'gateway';
  const setScope = (s: UsageScope) =>
    setParams(s === 'self-hosted' ? { scope: 'self-hosted' } : {}, { replace: true });

  return (
    <Stack>
      <SegmentedControl
        aria-label="Usage scope"
        value={scope}
        onValueChange={(v) => setScope(v as UsageScope)}
        items={[
          { value: 'gateway', label: 'Gateway-wide' },
          { value: 'self-hosted', label: 'Self-hosted streams' },
        ]}
      />
      {scope === 'gateway' ? <GatewayUsage serviceId={serviceId} /> : <SelfHostedUsage />}
    </Stack>
  );
}

export default UsageAnalytics;

// ─── Period selection (preset | month | custom day-range) ────────────────────

type PresetKey = '24h' | '7d' | '30d';
// The period is a quick preset, a calendar month, or an absolute start–end day
// window picked in the calendar — mirrors the CDN Business Usage switcher so a
// past World Cup / billing window can be pulled up (not just the last 30 days).
type RangeSel =
  | { kind: 'preset'; preset: PresetKey }
  | { kind: 'month'; month: string }
  | { kind: 'custom'; from: string; to: string };

const PRESET_LABEL: Record<PresetKey, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

interface MonthOpt {
  key: string; // YYYY-MM
  label: string; // "Jun 2026"
}
function buildMonths(): MonthOpt[] {
  const now = new Date();
  const out: MonthOpt[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    out.push({ key, label });
  }
  return out;
}
function monthToRange(key: string): { from: string; to: string } {
  const [y, m] = key.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1));
  const nextMonth = new Date(Date.UTC(y, m, 1));
  const now = new Date();
  const to = nextMonth.getTime() > now.getTime() ? now : nextMonth;
  return { from: start.toISOString(), to: to.toISOString() };
}

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

interface ResolvedRange {
  from: string;
  to: string;
  granularity: AesAnalyticsGranularity; // '1h' for a ≤ ~1-day window, else 'day'
  label: string;
  slug: string;
}
function resolveRange(sel: RangeSel, months: MonthOpt[]): ResolvedRange {
  const now = new Date();
  if (sel.kind === 'preset') {
    const hours = sel.preset === '24h' ? 24 : sel.preset === '7d' ? 24 * 7 : 24 * 30;
    const from = new Date(now.getTime() - hours * 3600_000).toISOString();
    return {
      from,
      to: now.toISOString(),
      granularity: sel.preset === '24h' ? '1h' : 'day',
      label: PRESET_LABEL[sel.preset],
      slug: sel.preset,
    };
  }
  if (sel.kind === 'month') {
    const { from, to } = monthToRange(sel.month);
    return {
      from,
      to,
      granularity: 'day',
      label: months.find((m) => m.key === sel.month)?.label ?? sel.month,
      slug: sel.month,
    };
  }
  // custom: sub-day windows read at 1h, multi-day at day.
  const spanMs = new Date(sel.to).getTime() - new Date(sel.from).getTime();
  return {
    from: sel.from,
    to: sel.to,
    granularity: spanMs <= 25 * 3600_000 ? '1h' : 'day',
    label: `${shortDate(sel.from)} – ${shortDate(sel.to)}`,
    slug: `${sel.from.slice(0, 10)}_${sel.to.slice(0, 10)}`,
  };
}

// ─── Metric card definitions ─────────────────────────────────────────────────

interface MetricDef {
  metric: AesAnalyticsMetric;
  title: string;
  subtitle: string;
  type: 'area' | 'bar' | 'line';
  color: string;
  /** Daily metrics ignore the period granularity and always read at 1d. */
  daily?: boolean;
}

// Active sessions is intentionally omitted: the gateway only recorded the
// sessions gauge for an early slice of the period, so its chart would show a
// truncated time axis next to the full-range keys/unique-IP charts.
const METRICS: MetricDef[] = [
  { metric: 'keys_issued', title: 'Keys issued', subtitle: 'AES-128 keyfiles served over time.', type: 'area', color: 'var(--vn-accent-solid)' },
  { metric: 'unique_ips', title: 'Unique IPs', subtitle: 'Distinct client IPs per day.', type: 'bar', color: 'var(--vn-info-solid)', daily: true },
];

/** Turn the flat series into chart rows keyed by the metric name; xfmt formats the
 *  x-axis label (the Chart renders xKey verbatim). */
function buildChartData(
  series: AesAnalyticsPoint[] | undefined,
  metric: AesAnalyticsMetric,
  xfmt: (v: string | number | undefined) => string,
): Array<Record<string, string | number>> {
  return (series ?? []).map((p) => ({ t: xfmt(p.timestamp), [metric]: p.value }));
}

function fmtTick(daily: boolean) {
  return (v: string | number | undefined): string =>
    daily ? dayjs(String(v)).format('MMM D') : dayjs(String(v)).format('MMM D HH:mm');
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const countFormatter = (v: string | number | undefined): string =>
  typeof v === 'number' ? v.toLocaleString() : String(v ?? '');

const sumOf = (s?: AesAnalyticsPoint[]): number => (s ?? []).reduce((a, p) => a + (p.value || 0), 0);
const maxOf = (s?: AesAnalyticsPoint[]): number => (s ?? []).reduce((a, p) => Math.max(a, p.value || 0), 0);

/** CSV of the current gateway view: one row per keys_issued bucket, with the
 *  matching unique-IP value (same day). Empty when there is nothing on screen. */
function gatewayCsv(
  keys: AesAnalyticsPoint[] | undefined,
  uniqueIps: AesAnalyticsPoint[] | undefined,
): Blob {
  const ipByDay = new Map((uniqueIps ?? []).map((p) => [p.timestamp.slice(0, 10), p.value]));
  const header = ['timestamp', 'keys_issued', 'unique_ips'];
  const rows = (keys ?? []).map((p) => [
    p.timestamp,
    String(p.value),
    ipByDay.has(p.timestamp.slice(0, 10)) ? String(ipByDay.get(p.timestamp.slice(0, 10))) : '',
  ]);
  return new Blob([[header, ...rows].map((r) => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
}

// ─── Gateway scope (datalake time-series) ────────────────────────────────────

function MetricChartCard({
  def,
  query,
  granularity,
}: {
  def: MetricDef;
  query: ReturnType<typeof useAesAnalytics>;
  granularity: AesAnalyticsGranularity;
}) {
  const { data, isLoading } = query;
  const tickFmt = fmtTick(granularity === 'day');
  const chartData = useMemo(
    () => buildChartData(data?.series, def.metric, tickFmt),
    [data?.series, def.metric, tickFmt],
  );

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{def.title}</CardTitle>
        <CardDescription>{def.subtitle}</CardDescription>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton variant="rect" height={240} />
        ) : chartData.length === 0 ? (
          <EmptyState size="sm" icon={<BarChart2 size={20} />} title="No data for this period" description="No data points recorded in the selected window." />
        ) : (
          <Chart
            type={def.type}
            data={chartData}
            xKey="t"
            series={[{ key: def.metric, label: def.title, color: def.color }]}
            height={240}
            // Full integer in the tooltip; abbreviated ticks on the Y-axis.
            valueFormatter={(v) => countFormatter(v)}
            yAxisFormatter={(v) => formatCount(v)}
          />
        )}
      </CardBody>
    </Card>
  );
}

function GatewayUsage({ serviceId }: { serviceId?: string }) {
  const months = useMemo(buildMonths, []);
  const [sel, setSel] = useState<RangeSel>({ kind: 'preset', preset: '30d' });
  const rng = useMemo(() => resolveRange(sel, months), [sel, months]);

  const keysIssued = useAesAnalytics(serviceId, { metric: 'keys_issued', granularity: rng.granularity, from: rng.from, to: rng.to });
  // unique_ips is stored at day grain only — the bucket selector doesn't apply.
  const uniqueIps = useAesAnalytics(serviceId, { metric: 'unique_ips', granularity: 'day', from: rng.from, to: rng.to });

  const queries: Record<string, ReturnType<typeof useAesAnalytics>> = {
    keys_issued: keysIssued,
    unique_ips: uniqueIps,
  };

  const refresh = () => {
    void keysIssued.refetch();
    void uniqueIps.refetch();
  };

  const selValue = sel.kind === 'preset' ? sel.preset : sel.kind === 'month' ? `m:${sel.month}` : '';
  const onSelect = (v: string) => {
    if (v.startsWith('m:')) setSel({ kind: 'month', month: v.slice(2) });
    else setSel({ kind: 'preset', preset: v as PresetKey });
  };

  const hasData = (keysIssued.data?.series?.length ?? 0) > 0;
  const exportCsv = () =>
    triggerDownload(
      gatewayCsv(keysIssued.data?.series, uniqueIps.data?.series),
      `aes-business-usage-${rng.slug}.csv`,
    );

  const statsLoading = keysIssued.isLoading || uniqueIps.isLoading;
  const kpis: Kpi[] = [
    { label: 'Keys issued', value: formatNum(sumOf(keysIssued.data?.series)), hint: `total · ${rng.label.toLowerCase()}` },
    { label: 'Peak unique IPs', value: formatNum(maxOf(uniqueIps.data?.series)), hint: 'busiest day' },
  ];

  return (
    <>
      <div className={styles.toolbar} style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Select value={selValue} onValueChange={onSelect}>
            <SelectTrigger size="md" style={{ width: 180, maxWidth: '100%', flexShrink: 1 }}>
              <SelectValue placeholder="Custom range" />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PRESET_LABEL) as PresetKey[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {PRESET_LABEL[p]}
                </SelectItem>
              ))}
              {months.map((m) => (
                <SelectItem key={m.key} value={`m:${m.key}`}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Preset/month-or-custom: the calendar (with the selected range highlighted
              + per-endpoint time) picks an absolute start–end window; applying it
              deselects the preset. The end time defaults to 23:59 so an end day counts
              through the whole of that day — same inclusive basis as the other views. */}
          <DateTimeRangePicker
            value={sel.kind === 'custom' ? { from: sel.from, to: sel.to } : { from: '', to: '' }}
            onChange={(v) => {
              if (v.from && v.to) setSel({ kind: 'custom', from: v.from, to: v.to });
            }}
            placeholder="Custom range"
          />
        </div>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="md" startIcon={<Download size={14} />}>
                Export
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!hasData} onSelect={exportCsv}>
                Download CSV (current view)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <RefreshControl onRefresh={refresh} />
        </div>
      </div>
      <KpiTiles items={kpis} columns={2} loading={statsLoading} />
      <div className={styles.chartGrid}>
        {METRICS.map((m) => (
          <MetricChartCard key={m.metric} def={m} query={queries[m.metric]} granularity={m.daily ? 'day' : rng.granularity} />
        ))}
      </div>
    </>
  );
}

// ─── Self-hosted scope (sig-control per-stream rollup) ───────────────────────

type ShPeriodKey = '24h' | '7d' | '30d';

const SH_PERIODS: Record<ShPeriodKey, { label: string; hours: number }> = {
  '24h': { label: 'Last 24h', hours: 24 },
  '7d': { label: 'Last 7d', hours: 24 * 7 },
  '30d': { label: 'Last 30d', hours: 24 * 30 },
};
const SH_PERIOD_ORDER: ShPeriodKey[] = ['24h', '7d', '30d'];

/** Sum one counter across every owned stream's row. */
const sumBy = (rows: DrmUsageRow[], pick: (r: DrmUsageRow) => number): number =>
  rows.reduce((acc, r) => acc + pick(r), 0);

function SelfHostedUsage() {
  const [period, setPeriod] = useState<ShPeriodKey>('24h');
  const { hours, label } = SH_PERIODS[period];

  const range = useMemo(() => {
    const to = dayjs();
    return { from: to.subtract(hours, 'hour').toISOString(), to: to.toISOString() };
  }, [hours]);

  const { data, isLoading, isError, error, refetch } = useDrmUsageSummary(range);
  const rows = data?.rows ?? [];

  const kpis: Kpi[] = [
    { label: 'Keys issued', value: formatNum(sumBy(rows, (r) => r.counters.keys_issued)), hint: `total · ${label.toLowerCase()}` },
    // Guard with ?? 0: the rollup omits a counter for streams that have none yet,
    // so an unguarded sum would go NaN and render "—".
    { label: 'Requests', value: formatNum(sumBy(rows, (r) => r.counters.requests ?? 0)), hint: 'key-delivery requests' },
    { label: 'Unique IPs', value: formatNum(sumBy(rows, (r) => r.unique_ips)), hint: 'across streams' },
    { label: 'Rejects', value: formatNum(sumBy(rows, (r) => r.counters.rejects ?? 0)), hint: 'auth + 4xx' },
  ];

  // Per-stream keys issued, busiest first (top 12 keeps the bar list readable).
  const bars: BarListItem[] = rows
    .map((r) => ({ label: r.stream_id, value: r.counters.keys_issued, display: formatNum(r.counters.keys_issued) }))
    .filter((b) => b.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);

  const errMsg = (error as { message?: string })?.message ?? 'The sig-control /usage/summary endpoint is unreachable.';

  return (
    <Stack>
      <div className={drmStyles.usageToolbar}>
        <SegmentedControl
          aria-label="Time period"
          value={period}
          onValueChange={(v) => setPeriod(v as ShPeriodKey)}
          items={SH_PERIOD_ORDER.map((p) => ({ value: p, label: SH_PERIODS[p].label }))}
        />
        <RefreshControl onRefresh={() => void refetch()} />
      </div>

      <KpiTiles items={kpis} columns={4} loading={isLoading} />

      <Card flat>
        <CardHeader>
          <CardTitle>Keys issued per stream</CardTitle>
          <CardDescription>AES-128 keyfiles delivered per owned stream, busiest first.</CardDescription>
        </CardHeader>
        <CardBody>
          {isError ? (
            <EmptyState title="Could not load usage" description={errMsg} />
          ) : isLoading ? (
            <Skeleton variant="rect" height={200} />
          ) : bars.length === 0 ? (
            <EmptyState size="sm" icon={<BarChart2 size={20} />} title="No usage for this period" description="No keys issued in the selected window." />
          ) : (
            <BarList items={bars} />
          )}
        </CardBody>
      </Card>
    </Stack>
  );
}
